import { canNavigate, ConductorOneChildActive, Router, ScreenBase, splitUrlSegments } from "@frui.ts/screens";
import EventBus, { orderIsPaid, paymentMultiFuelTypeNotAllowed } from "services/eventBus";
import BankTransferViewModel, { BankTransferNavigationName } from "../common/payment/bankTransferViewModel";
import StatusViewModel, { StatusViewNavigationName } from "../common/payment/statusViewModel";
import BatchOrderViewModel, { BatchOrderViewModelNavigationName } from "./batchOrderViewModel";
import OrderRecapViewModel from "./orderRecapViewModel";
import OutageService from "../../services/outageService";
import { action, computed, reaction, when } from "mobx";
import OrderOutageViewModel from "../common/orderOutage/orderOutageViewModel";
import { SystemComponent } from "../../models/outage";
import OutagesViewModel from "../common/outagesViewModel";
import ChargeOrderService from "services/chargeOrderService";
import ChargeOrder, { ChargeOrderStatus } from "models/chargeOrder";
import EnumService from "services/enumService";
import ContactMultiEshopViewModel from "./contactMultiEshopViewModel";
import ChargeTypesService from "services/chargeTypesService";
import PaymentMultiEshopViewModel from "./paymentMultiEshopViewModel";
import ChargeOrderContactItem from "models/chargeOrderContactItem";
import TimeService from "services/timeService";
import PaymentResult from "models/paymentResult";
import PaymentRetryViewModel, {
  clearRetryConsumed,
  isRetryBlocked,
  markRetryConsumed,
  PaymentRetryNavigationName,
} from "../common/payment/paymentRetryViewModel";
import MultiPaymentRetryViewModel from "../common/payment/multiPaymentRetryViewModel";
import PaymentService from "services/payment/paymentService";
import ChargeOrderRepository from "repositories/chargeOrderRepository";
import { PayGatePaymentStatus } from "models/payGatePaymentResponse";

export const MultiEshopModuleNavigationName = "multi_eshop";

@Router.registerRoute({
  name: Router.Self,
  route: MultiEshopModuleNavigationName,
  children: [
    BatchOrderViewModel,
    OrderRecapViewModel,
    ContactMultiEshopViewModel,
    PaymentMultiEshopViewModel,
    StatusViewModel,
    BankTransferViewModel,
    MultiPaymentRetryViewModel,
  ],
})
export default class MultiEshopViewModel extends ConductorOneChildActive<ScreenBase> {
  private unsubscriptions: Array<() => void> = [];
  public contactMultiEshopViewModel: ContactMultiEshopViewModel;
  public paymentMultiEshopViewModel: PaymentMultiEshopViewModel;
  payGateOutagesViewModel: OutagesViewModel;
  eshopOutagesVieModel: OutagesViewModel;

  @computed
  get eshopOutage() {
    return this.outageService.eshopActiveOutage;
  }

  @computed
  get showOutages() {
    return !(this.activeChild instanceof OrderOutageViewModel) && !(this.activeChild instanceof StatusViewModel);
  }

  constructor(
    public enumService: EnumService,
    private chargeOrderService: ChargeOrderService,
    private timeService: TimeService,
    private eventBus: EventBus,
    private batchOrderViewModel: BatchOrderViewModel,
    private orderRecapViewModel: OrderRecapViewModel,
    private chargeTypesService: ChargeTypesService,
    private paymentMultiEshopViewModelFactory: ReturnType<typeof PaymentMultiEshopViewModel.Factory>,
    private outageService: OutageService,
    private bankTransferVMFactory: ReturnType<typeof BankTransferViewModel.Factory>,
    private statusVMFactory: ReturnType<typeof StatusViewModel.Factory>,
    private orderOutageViewModelFactory: ReturnType<typeof OrderOutageViewModel.Factory>,
    private contactMultiEshopViewModelFactory: ReturnType<typeof ContactMultiEshopViewModel.Factory>,
    private outagesViewModelFactory: ReturnType<typeof OutagesViewModel.Factory>,
    private paymentRetryVMFactory: ReturnType<typeof PaymentRetryViewModel.Factory>,
    private paymentService: PaymentService,
    private chargeOrderRepository: ChargeOrderRepository
  ) {
    super();
    this.navigationName = MultiEshopModuleNavigationName;
    this.nameValue = "multiEshop.title";
    this.payGateOutagesViewModel = this.outagesViewModelFactory(SystemComponent.PAY_GATE);
    this.eshopOutagesVieModel = this.outagesViewModelFactory(SystemComponent.ESHOP);
    this.children.push(
      this.batchOrderViewModel,
      this.orderRecapViewModel,
      (this.contactMultiEshopViewModel = this.contactMultiEshopViewModelFactory(
        new ChargeOrderContactItem(this.chargeOrder.notificationPreferences)
      )),
      (this.paymentMultiEshopViewModel = this.paymentMultiEshopViewModelFactory(this.chargeOrder))
    );
  }

  protected async onInitialize(): Promise<any> {
    await this.enumService.startInitialization();
    await this.timeService.getCurrentServerTime();

    await this.chargeTypesService.startInitialization();
    this.unsubscriptions.push(
      this.eventBus.subscribe(orderIsPaid, this.onOrderIsPaid),
      this.eventBus.subscribe(paymentMultiFuelTypeNotAllowed, this.onPaymentMultiFuelTypeNotAllowed),
      reaction(() => this.eshopOutage, this.onEshopOutageChanged)
    );

    return super.onInitialize();
  }

  protected async onActivate() {
    // Symmetric to orderWizardModel.onActivate: wipe leftovers from the single flow
    // before the bulk flow starts.
    if (this.chargeOrderService.chargeOrder._origin === "single") {
      this.chargeOrderService.reset();
    }

    if (!this.activeChild || this.chargeOrderService.orderIsEmptyOrIncomplete) {
      if (!window.location.hash.includes(`/${PaymentRetryNavigationName}/`)) {
        this.lazyCheckEshopOutage();
        await this.tryActivateChild(this.batchOrderViewModel);
      }
    }

    return super.onActivate();
  }

  @action.bound
  protected async changeActiveChild(newChild: ScreenBase | undefined, closePrevious: boolean): Promise<any> {
    if (this.activeChild instanceof StatusViewModel && newChild instanceof BatchOrderViewModel) {
      this.chargeOrderService.reset();
    }
    super.changeActiveChild(newChild, closePrevious);
  }

  protected onDeactivate(isClosing: boolean): Promise<void> {
    if (isClosing) {
      this.unsubscriptions.forEach(unsubscribe => unsubscribe());
      this.unsubscriptions = [];
    }
    return super.onDeactivate(isClosing);
  }

  // tslint:disable-next-line:cognitive-complexity
  async navigate(subPath: string | undefined, params: any) {
    const segments = splitUrlSegments(subPath);

    // Vymažeme consumed flag před kontrolou — čerstvé načtení (změna jazyka, F5)
    // musí vždy projít. Pohyb zpět zachytí popstate handler ještě před tímto kódem.
    if (segments.length > 1 && segments[0] === PaymentRetryNavigationName && segments[1]) {
      clearRetryConsumed(segments[1]);
    }
    // Tato podmínka je nyní prakticky vždy false (flag byl právě vymazán výše),
    // ponecháme ji jako bezpečnostní síť pro případ selhání clearRetryConsumed.
    if (segments.length > 1 && segments[0] === PaymentRetryNavigationName && segments[1] && isRetryBlocked(segments[1])) {
      await this.tryActivateChild(this.batchOrderViewModel);
      return;
    }

    let childToActivate;
    if (segments[0] === BankTransferNavigationName) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments[0] === PaymentRetryNavigationName && segments[1]) {
      childToActivate = await this.findNavigationChild(subPath || "");
    } else if (segments[0] === StatusViewNavigationName && segments[1]) {
      const storedOrder = await this.chargeOrderService.getStoredChargeOrder(segments[1]);
      const chargeOrder = storedOrder ?? this.chargeOrderService.chargeOrder;

      if (storedOrder) {
        storedOrder._origin = "multiple";
        this.chargeOrderService.chargeOrder = storedOrder;
      }

      const result = params.result;
      const failureType = params["failure_type"];
      const failureOrigin = params["failure_origin"];

      if (chargeOrder) {
        await this.chargeOrderService.updateChargeOrderStatus(result, chargeOrder, segments[1]);
      }

      if (chargeOrder?.id) {
        const retryChild = await this.tryCreatePaymentRetryChild(chargeOrder);
        if (retryChild) {
          childToActivate = retryChild;
        } else {
          this.batchOrderViewModel.paymentResultFailureType = failureType;
          this.batchOrderViewModel.paymentResultFailureOrigin = failureOrigin;
          childToActivate = await this.findNavigationChild(subPath || "");
        }
      } else {
        this.batchOrderViewModel.paymentResultFailureType = failureType;
        this.batchOrderViewModel.paymentResultFailureOrigin = failureOrigin;
        childToActivate = await this.findNavigationChild(subPath || "");
      }
    } else {
      childToActivate = await this.findNavigationChild(segments[0] || "");
    }

    if (segments[0] !== BatchOrderViewModelNavigationName && this.activeChild instanceof BatchOrderViewModel) {
      const isValid = this.activeChild.validateAll();

      if (!isValid) return;
    }

    if (childToActivate) {
      await this.tryActivateChild(childToActivate);

      if (canNavigate(childToActivate)) {
        await childToActivate.navigate(segments[1], params);
      }
    }

    await this.onChildNavigated(childToActivate);
  }

  async findNavigationChild(navigationName: string) {
    const retrySegments = splitUrlSegments(navigationName);
    if (retrySegments[0] === PaymentRetryNavigationName && retrySegments.length > 1 && retrySegments[1]) {
      const orderId = retrySegments[1];
      if (isRetryBlocked(orderId)) {
        return super.findNavigationChild(MultiEshopModuleNavigationName);
      }
      let chargeOrder = this.chargeOrderService.chargeOrder;
      if (!chargeOrder || chargeOrder.id !== orderId) {
        const stored = await this.chargeOrderService.getStoredChargeOrder(orderId);
        if (stored) {
          stored._origin = "multiple";
          this.chargeOrderService.chargeOrder = stored;
          chargeOrder = stored;
        }
      }
      if (chargeOrder?.id) {
        return this.buildPaymentRetryChild(chargeOrder);
      }
    }
    if (navigationName.includes(BankTransferNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments[1]) {
        const chargeOrder = await this.chargeOrderService.getStoredChargeOrder(segments[1]);
        if (chargeOrder && chargeOrder._status === ChargeOrderStatus.PaidBankTransfer) {
          return this.bankTransferVMFactory(chargeOrder);
        }
      }
    } else if (navigationName.includes(StatusViewNavigationName)) {
      const segments = splitUrlSegments(navigationName);
      if (segments[1]) {
        const orderId = segments[1];
        let chargeOrder = this.chargeOrderService.chargeOrder;

        if (!chargeOrder || chargeOrder.id !== orderId) {
          const storedOrder = await this.chargeOrderService.getStoredChargeOrder(orderId);
          if (storedOrder) {
            this.chargeOrderService.chargeOrder = storedOrder;
            chargeOrder = storedOrder;
          }
        }

        // Ask BE for the authoritative status using the id from the URL.
        let beStatus: ChargeOrderStatus | undefined;
        try {
          const remote = await this.chargeOrderRepository.get(orderId);
          beStatus = remote?.status;
        } catch {
          beStatus = undefined;
        }

        // Local status was just set by navigate() based on the URL `result` query param.
        // Trust it as a fallback when BE hasn't caught up yet
        // (race after second-tab payment_confirmation roundtrip).
        const localStatus = chargeOrder?._status;
        const isFinalStatus = (s?: ChargeOrderStatus) => s === ChargeOrderStatus.Paid || s === ChargeOrderStatus.Deferred;

        if (chargeOrder && (isFinalStatus(beStatus) || isFinalStatus(localStatus))) {
          const finalStatus = isFinalStatus(beStatus) ? beStatus! : localStatus!;
          chargeOrder._status = finalStatus;
          this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
          return this.statusVMFactory(chargeOrder);
        } else {
          return super.findNavigationChild(MultiEshopModuleNavigationName);
        }
      }
    }
    return super.findNavigationChild(navigationName);
  }

  private async lazyCheckEshopOutage() {
    await when(() => this.outageService.isInitialized === true);
    if (this.eshopOutage) {
      this.tryActivateChild(this.orderOutageViewModelFactory(this.eshopOutage));
    }
  }

  @computed
  get chargeOrder() {
    return this.chargeOrderService.chargeOrder;
  }

  @action.bound
  private onEshopOutageChanged() {
    if (this.eshopOutage === undefined) {
      this.tryActivateChild(this.batchOrderViewModel);
    }
  }

  @action.bound
  private onOrderIsPaid() {
    this.tryActivateChild(this.batchOrderViewModel);
  }

  @action.bound
  private async tryCreatePaymentRetryChild(chargeOrder: ChargeOrder): Promise<ScreenBase | undefined> {
    if (chargeOrder._status !== ChargeOrderStatus.Failed) return undefined;
    try {
      const payGatePayment = await this.chargeOrderRepository.getPayGatePayment(chargeOrder.id);

      if (payGatePayment?.permittedActions?.paymentRetry?.isAllowed) {
        return this.buildPaymentRetryChild(chargeOrder, payGatePayment);
      }

      // If payment is still in progress, show existing deferred screen
      if (payGatePayment?.status === PayGatePaymentStatus.IN_PROGRESS) {
        chargeOrder._status = ChargeOrderStatus.Deferred;
        this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
        return this.statusVMFactory(chargeOrder);
      }
    } catch {
      // If endpoint fails, fall back to existing behavior
    }
    return undefined;
  }

  private buildPaymentRetryChild(chargeOrder: ChargeOrder, payGatePayment?: any) {
    clearRetryConsumed(chargeOrder.id);
    const retryVM = this.paymentRetryVMFactory(
      chargeOrder,
      () => this.handleRetryPayment(chargeOrder),
      () => this.handleBackToPurchaseWithPrefill(),
      () => this.handlePaymentSucceeded(chargeOrder),
      () => this.handlePaymentInProgress(chargeOrder),
      paymentData => this.handleRetryGooglePayPayment(chargeOrder, paymentData),
      () => this.handleRetryIdleTimeout(chargeOrder)
    );
    if (payGatePayment) retryVM.payGatePaymentResponse = payGatePayment;
    return retryVM;
  }

  @action.bound
  private handleRetryIdleTimeout(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    this.chargeOrderService.reset();
    window.location.href = this.batchOrderViewModel.localizationService.translateGeneral(
      "eshop.idle.timeoutRedirect",
      undefined,
      true
    );
  }

  @action.bound
  private async handleRetryGooglePayPayment(chargeOrder: ChargeOrder, paymentData: google.payments.api.PaymentData) {
    try {
      await this.paymentService.submitGooglePayment(chargeOrder.id, paymentData);
    } catch {
      // submit failure events are handled by retry VM's own subscribers.
    }
  }

  @action.bound
  private async handleRetryPayment(chargeOrder: ChargeOrder) {
    await this.paymentService.payGateRedirect(chargeOrder.id, MultiEshopModuleNavigationName);
  }

  @action.bound
  private async handlePaymentSucceeded(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    await this.chargeOrderService.updateChargeOrderStatus(PaymentResult.SUCCESS, chargeOrder, chargeOrder.id);
    await this.tryActivateChild(this.statusVMFactory(chargeOrder));
  }

  @action.bound
  private async handlePaymentInProgress(chargeOrder: ChargeOrder) {
    markRetryConsumed(chargeOrder.id);
    chargeOrder._status = ChargeOrderStatus.Deferred;
    this.chargeOrderService.persistStoredChargeOrder(chargeOrder);
    await this.tryActivateChild(this.statusVMFactory(chargeOrder));
  }

  @action.bound
  private handleBackToPurchaseWithPrefill() {
    this.chargeOrderService.reopenOrder();
    this.tryActivateChild(this.batchOrderViewModel);
  }

  @action.bound
  private onPaymentMultiFuelTypeNotAllowed() {
    this.chargeOrderService.chargeOrder.charges.forEach(charge => (charge._isModified = true));
    this.chargeOrderService.persistChargeOrder();
    this.tryActivateChild(this.orderRecapViewModel);
  }
}
